import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  EnvelopeSimple, 
  Lock, 
  Wrench,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubcontractorLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email, code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('subcontractor_token');
    if (token) {
      navigate('/subcontractor/orders');
    }
  }, [navigate]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (res.ok) {
        setStep('code');
        toast.success('Inloggningskod skickad till din e-post');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Kunde inte skicka inloggningskod');
      }
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subcontractor/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('subcontractor_token', data.token);
        localStorage.setItem('subcontractor_email', data.email);
        toast.success('Inloggad!');
        navigate('/subcontractor/orders');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Ogiltig kod');
      }
    } catch (error) {
      toast.error('Ett fel uppstod');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="subcontractor-login-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-900 flex items-center justify-center">
            <Wrench size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Leverantörsportal</h1>
          <p className="text-slate-500 mt-2">Förvaltningen</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-8">
          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div className="text-center mb-6">
                <EnvelopeSimple size={40} className="mx-auto text-slate-400 mb-3" />
                <h2 className="text-lg font-medium text-slate-900">Logga in</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Ange din e-postadress för att få en inloggningskod
                </p>
              </div>

              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="din@email.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="subcontractor-email-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="subcontractor-request-code-btn"
              >
                {loading ? 'Skickar...' : 'Skicka inloggningskod'}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div className="text-center mb-6">
                <Lock size={40} className="mx-auto text-slate-400 mb-3" />
                <h2 className="text-lg font-medium text-slate-900">Ange din kod</h2>
                <p className="text-sm text-slate-500 mt-1">
                  En 6-siffrig kod har skickats till<br />
                  <span className="font-medium text-slate-700">{email}</span>
                </p>
              </div>

              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoFocus
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="subcontractor-code-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || code.length !== 6}
                data-testid="subcontractor-verify-code-btn"
              >
                {loading ? 'Verifierar...' : 'Logga in'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Ändra e-postadress
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Endast för registrerade underleverantörer
        </p>
      </div>
    </div>
  );
}
